import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { PlayIcon, SquareIcon, RotateCcwIcon } from 'lucide-react';

type ResourceStatus = 'started' | 'stopped' | 'starting' | 'stopping' | 'missing';

type ResourceManifest = {
  version?: string;
  author?: string;
  description?: string;
  fx_version?: string;
  games?: string[];
  dependencies?: string[];
};

type Resource = {
  name: string;
  status: ResourceStatus;
  ms?: number;
  tick?: number;
  category?: string;
  manifest?: ResourceManifest;
};

const STATUS_CONFIG: Record<ResourceStatus, { label: string; color: string }> = {
  started:  { label: 'Started',  color: '#00ff66' },
  stopped:  { label: 'Stopped',  color: '#4a6680' },
  starting: { label: 'Starting', color: '#fbbf24' },
  stopping: { label: 'Stopping', color: '#f87171' },
  missing:  { label: 'Missing',  color: '#ef4444' },
};

type Props = {
  resource: Resource | null;
  open: boolean;
  onClose: () => void;
  onAction: (name: string, action: 'start' | 'stop' | 'restart') => void;
  actionTarget: string | null;
};

function tickColor(ms?: number): string {
  if (!ms) return 'var(--sc-muted)';
  if (ms < 5)  return '#00ff66';
  if (ms < 15) return '#fbbf24';
  return '#f87171';
}

export function ResourceDetailDrawer({ resource, open, onClose, onAction, actionTarget }: Props) {
  if (!resource) return null;
  const cfg = STATUS_CONFIG[resource.status] ?? STATUS_CONFIG.stopped;
  const isPending = actionTarget?.startsWith(`${resource.name}:`);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md flex flex-col gap-0 p-0 border-l"
        style={{ background: 'var(--sc-bg-surface)', borderColor: 'var(--sc-border)' }}
      >
        {/* Top accent */}
        <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-[var(--sc-cyan)] to-transparent" />

        <SheetHeader className="px-5 pt-5 pb-4 border-b" style={{ borderColor: 'var(--sc-border)' }}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <SheetTitle className="text-base font-black font-mono truncate" style={{ color: 'var(--sc-text)' }}>
                {resource.name}
              </SheetTitle>
              {resource.category && (
                <div className="text-[10px] font-mono mt-0.5" style={{ color: 'var(--sc-muted)' }}>
                  {resource.category}
                </div>
              )}
            </div>
            <span className="shrink-0 text-[9px] font-mono font-bold px-2 py-1 rounded border"
              style={{ color: cfg.color, background: cfg.color + '14', borderColor: cfg.color + '30' }}>
              {cfg.label}
            </span>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-5">
          {/* Performance */}
          {resource.status === 'started' && (
            <Section title="Performance">
              <div className="grid grid-cols-2 gap-3">
                <Stat label="Tick Time" value={resource.tick !== undefined ? `${resource.tick.toFixed(2)}ms` : '—'} color={tickColor(resource.tick)} />
                <Stat label="Avg ms" value={resource.ms !== undefined ? `${resource.ms.toFixed(2)}ms` : '—'} color={tickColor(resource.ms)} />
              </div>
            </Section>
          )}

          {/* Manifest */}
          {resource.manifest && (
            <Section title="Manifest">
              <div className="flex flex-col gap-2">
                {resource.manifest.version && <Row label="Version" value={resource.manifest.version} />}
                {resource.manifest.author  && <Row label="Author"  value={resource.manifest.author} />}
                {resource.manifest.fx_version && <Row label="FX Version" value={resource.manifest.fx_version} />}
                {resource.manifest.games?.length ? <Row label="Games" value={resource.manifest.games.join(', ')} /> : null}
                {resource.manifest.description && (
                  <div className="mt-1">
                    <div className="text-[9px] font-mono uppercase tracking-wider mb-1" style={{ color: 'var(--sc-muted)' }}>Description</div>
                    <p className="text-xs font-mono leading-relaxed" style={{ color: 'var(--sc-text)' }}>{resource.manifest.description}</p>
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* Dependencies */}
          {resource.manifest?.dependencies?.length ? (
            <Section title={`Dependencies (${resource.manifest.dependencies.length})`}>
              <div className="flex flex-col gap-1">
                {resource.manifest.dependencies.map((dep) => (
                  <div key={dep} className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-mono"
                    style={{ background: 'var(--sc-bg-elevated)', color: 'var(--sc-cyan)' }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--sc-cyan)] shrink-0" />
                    {dep}
                  </div>
                ))}
              </div>
            </Section>
          ) : null}
        </div>

        {/* Action buttons */}
        <div className="px-5 pb-5 pt-3 border-t flex gap-2" style={{ borderColor: 'var(--sc-border)' }}>
          {resource.status === 'stopped' || resource.status === 'missing' ? (
            <button disabled={!!isPending} onClick={() => onAction(resource.name, 'start')}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wide transition-all disabled:opacity-50"
              style={{ background: 'rgba(0,255,102,0.12)', color: '#00ff66', border: '1px solid rgba(0,255,102,0.25)' }}>
              <PlayIcon className="h-3.5 w-3.5" /> Start
            </button>
          ) : (
            <>
              <button disabled={!!isPending} onClick={() => onAction(resource.name, 'restart')}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wide transition-all disabled:opacity-50"
                style={{ background: 'rgba(0,255,209,0.08)', color: 'var(--sc-cyan)', border: '1px solid var(--sc-border-mid)' }}>
                <RotateCcwIcon className="h-3.5 w-3.5" /> Restart
              </button>
              <button disabled={!!isPending} onClick={() => onAction(resource.name, 'stop')}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wide transition-all disabled:opacity-50"
                style={{ background: 'rgba(239,68,68,0.08)', color: '#f87171', border: '1px solid rgba(239,68,68,0.2)' }}>
                <SquareIcon className="h-3.5 w-3.5" /> Stop
              </button>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[9px] font-mono font-bold uppercase tracking-[0.15em] mb-2" style={{ color: 'var(--sc-muted)' }}>{title}</div>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b" style={{ borderColor: 'var(--sc-border)' }}>
      <span className="text-[10px] font-mono" style={{ color: 'var(--sc-muted)' }}>{label}</span>
      <span className="text-[10px] font-mono font-semibold" style={{ color: 'var(--sc-text)' }}>{value}</span>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="rounded-lg p-3" style={{ background: 'var(--sc-bg-elevated)', border: '1px solid var(--sc-border)' }}>
      <div className="text-[9px] font-mono uppercase tracking-wider mb-1" style={{ color: 'var(--sc-muted)' }}>{label}</div>
      <div className="text-sm font-black font-mono" style={{ color }}>{value}</div>
    </div>
  );
}
